import { html, nothing, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "@/common/directives/when";
import { EkoElement } from "@/utilities/element";
import style from "./pagination.css?inline";

@customElement("eko-pagination")
class Pagination extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  @property({ reflect: false, type: Number }) items: number = 0;
  @property({ reflect: false, type: Number }) itemsPerPage: number = 0;
  @property({ reflect: false, type: Number }) page: number = 1;
  @property({ reflect: true }) size: "sm" | "md" | "lg" = "md";

  private getVisiblePages(): (number | string)[] {
    if (this.pageCount <= 7) {
      // If 7 or fewer pages, show all
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const current = this.page;
    const total = this.pageCount;

    // Always show first page
    pages.push(1);

    if (current <= 4) {
      // Current page is near the beginning (pages 1-4)
      // Show: 1, 2, 3, 4, 5, ..., last
      for (let i = 2; i <= 5; i++) {
        if (i < total) {
          pages.push(i);
        }
      }
      if (total > 6) {
        pages.push("...");
      }
      if (total > 5) {
        pages.push(total);
      }
    } else if (current >= total - 3) {
      // Current page is near the end (last 4 pages)
      // Show: 1, ..., last-4, last-3, last-2, last-1, last
      if (total > 6) {
        pages.push("...");
      }
      for (let i = Math.max(2, total - 4); i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Current page is in the middle
      // Show: 1, ..., current-1, current, current+1, ..., last
      pages.push("...");
      pages.push(current - 1);
      pages.push(current);
      pages.push(current + 1);
      pages.push("...");
      pages.push(total);
    }

    return pages;
  }

  private handlePageClick(page: number) {
    if (page !== this.page && page >= 1 && page <= this.pageCount) {
      this.page = page;
      this.dispatchEvent(
        new CustomEvent("change", {
          bubbles: true,
          detail: {
            currentPage: page,
            nextPage: page + 1 <= this.pageCount ? page + 1 : null,
            previousPage: page - 1 >= 1 ? page - 1 : null,
          },
        })
      );
    }
  }

  render() {
    const visiblePages = this.getVisiblePages();

    return this.pageCount > 1
      ? html`
          <eko-button
            @click=${() => this.handlePageClick(this.page - 1)}
            disabled=${when(this.page === 1)}
            part="nav previous">
            <eko-icon slot="before" name="arrow-left" part="nav-icon"></eko-icon>
            Previous</eko-button
          >
          <div class="eko-pagination__pages" part="pages">
            ${repeat(
              visiblePages,
              (item) => item,
              (item) => {
                if (item === "...") {
                  return html`<span class="eko-pagination__ellipsis" part="ellipsis">...</span>`;
                }

                const page = item as number;
                const isActive = page === this.page;

                return html`
                  <eko-button
                    class="eko-pagination__page"
                    part="page"
                    variant="plain"
                    @click=${() => this.handlePageClick(page)}
                    aria-current=${isActive ? "page" : "false"}
                    aria-label=${`Go to page ${page}`}>
                    ${page}
                  </eko-button>
                `;
              }
            )}
          </div>
          <eko-button
            @click=${() => this.handlePageClick(this.page + 1)}
            disabled=${when(this.page === this.pageCount)}
            part="nav next">
            Next
            <eko-icon slot="after" name="arrow-right" part="nav-icon"></eko-icon>
          </eko-button>
        `
      : nothing;
  }

  get pageCount() {
    return Math.ceil(this.items / this.itemsPerPage);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-pagination": Pagination;
  }
}

export default Pagination;
